package kz.dentalcrm.app.core.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.vectorResource

@Stable
@Composable
fun UITextInputClickable(
    modifier: Modifier,
    enabled: Boolean = true,
    borderWidth: Dp = 1.dp,

    isOnError: Boolean = false,
    errorText: String = "",
    errorIconId: DrawableResource? = null,

    leadingIcon: ImageVector? = null,
    leadingIconSize: Dp = 16.dp,

    trailingIcon: ImageVector? = null,
    trailingIconSize: Dp = 16.dp,
    trailingIconClicked: () -> Unit = { },

    label: String = "",
    hint: String = "",
    hintIconId: DrawableResource? = null,
    isOnlyNumbers: Boolean = false,
    maxLines: Int = 1,
    maxLength: Int? = null,
    defaultValue: String = "",
    cornerShape: Dp = 16.dp,
    readOnly: Boolean = true,
    singleLine: Boolean = true,
    backgroundColor: Color = DefaultTheme.colors.surfaceInput,
    trimInput: Boolean = true,
    trailingActionClear: Boolean = true,
    keyboardType: KeyboardType = KeyboardType.Text,
    keyboardOptions: KeyboardOptions = KeyboardOptions(imeAction = ImeAction.Next).copy(
        keyboardType = keyboardType
    ),
    additionalPaddingValues: PaddingValues = PaddingValues(vertical = 4.dp, horizontal = 8.dp),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    visualTransformation: VisualTransformation? = null,
    valueChanged: (value: String) -> Unit,
    onClicked: () -> Unit,
) {

    var textState = defaultValue
    var isFocused by remember { mutableStateOf(false) }

    val onlyNumbers by remember { mutableStateOf(isOnlyNumbers) }

    val hintVisible by remember { mutableStateOf(hint.isNotEmpty()) }

    val labelStyle = when {
        isFocused -> DefaultTheme.typography.labelRegular
        textState.isNotEmpty() -> DefaultTheme.typography.labelRegular
        else -> DefaultTheme.typography.bodyMediumMedium
    }
    val borderColor = when {
        isOnError -> DefaultTheme.colors.error
        isFocused -> DefaultTheme.colors.primary
        else -> DefaultTheme.colors.outline
    }

    Column {
        Box(modifier = modifier) {
            TextField(
                modifier = modifier
                    .background(
                        color = backgroundColor,
                        shape = RoundedCornerShape(
                            cornerShape
                        )
                    )
                    .border(
                        width = borderWidth,
                        color = borderColor,
                        shape = RoundedCornerShape(
                            cornerShape
                        )
                    )
                    .focusable(enabled = false),
                colors = TextFieldDefaults.colors(
                    focusedContainerColor = Color.Transparent,
                    unfocusedContainerColor = Color.Transparent,
                    disabledContainerColor = Color.Transparent,
                    errorContainerColor = Color.Transparent,
                    errorLeadingIconColor = Color.Transparent,
                    errorTrailingIconColor = Color.Transparent,
                    errorIndicatorColor = Color.Transparent,
                    disabledIndicatorColor = Color.Transparent,
                    disabledLeadingIconColor = Color.Transparent,
                    disabledTrailingIconColor = Color.Transparent,
                    focusedIndicatorColor = Color.Transparent,
                    unfocusedIndicatorColor = Color.Transparent
                ),
                shape = RoundedCornerShape(cornerShape),
                textStyle = DefaultTheme.typography.bodyMediumMedium.copy(
                    color = DefaultTheme.colors.onSurface,
                ),
                leadingIcon = leadingIcon?.let {
                    {
                        Icon(
                            imageVector = leadingIcon,
                            contentDescription = null,
                            tint = borderColor,
                            modifier = Modifier.size(leadingIconSize)
                        )
                    }
                },
                label = if (label.isNotEmpty()) {
                    {
                        Text(
                            text = label,
                            style = labelStyle,
                            color = borderColor
                        )
                    }
                } else null,
                trailingIcon = trailingIcon?.let {
                    {
                        Icon(
                            imageVector = trailingIcon,
                            contentDescription = null,
                            tint = borderColor,
                            modifier = Modifier.size(trailingIconSize).clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = if (
                                    trailingActionClear && textState.isNotEmpty()
                                ) {
                                    {
                                        textState = ""
                                        valueChanged("")
                                    }
                                } else {
                                    trailingIconClicked
                                }
                            )
                        )
                    }
                },
                visualTransformation = visualTransformation ?: VisualTransformation.None,
                enabled = enabled,
                value = textState,
                maxLines = maxLines,
                singleLine = singleLine,
                keyboardOptions = keyboardOptions,
                keyboardActions = keyboardActions,
                readOnly = readOnly,
                onValueChange = { valueChanged ->
                    val value = if (!trimInput) valueChanged.trim() else valueChanged

                    val res = if (onlyNumbers) {
                        value.filter { symbol -> symbol.isDigit() }
                    } else {
                        value
                    }
                    (maxLength?.let {
                        valueChanged(manageLength(res, it)).also {
                            textState = manageLength(res, maxLength)
                        }
                    } ?: run {
                        valueChanged(res)
                        textState = res
                    })
                },
            )
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .clip(
                        shape = RoundedCornerShape(
                            cornerShape
                        )
                    )
                    .focusable(true)
                    .clickable {
                        onClicked()
                    }
                    .width(intrinsicSize = IntrinsicSize.Min)
            )
        }

        AnimatedVisibility(visible = hintVisible) {
            Spacer(
                modifier = Modifier
                    .widthIn()
                    .height(12.dp)
            )
            Row(
                modifier = Modifier.padding(additionalPaddingValues)
            ) {
                hintIconId?.let {
                    Icon(
                        imageVector = vectorResource(hintIconId),
                        contentDescription = null
                    )
                }
                Spacer(
                    modifier = Modifier
                        .width(8.dp)
                )
                Text(
                    text = hint,
                    textAlign = TextAlign.Start,
                    style = DefaultTheme.typography.labelRegular,
                )
            }
        }
        AnimatedVisibility(visible = isOnError) {
            Spacer(
                modifier = Modifier
                    .widthIn()
                    .height(12.dp)
            )
            Row(
                modifier = Modifier.padding(additionalPaddingValues)
            ) {
                errorIconId?.let {
                    Icon(
                        imageVector = vectorResource(errorIconId),
                        contentDescription = null
                    )
                }
                Spacer(
                    modifier = Modifier
                        .width(8.dp)
                )
                Text(
                    text = errorText,
                    textAlign = TextAlign.Start,
                    color = DefaultTheme.colors.error,
                    style = DefaultTheme.typography.labelRegular,
                )
            }
        }
    }
}

private fun manageLength(input: String, length: Int) =
    if (input.length > length) input.substring(0, length) else input
